import moment from "moment"; // Import moment for date/time manipulation
import React, { useEffect, useRef, useState } from "react"; // Import React and hooks
import toast from "react-hot-toast"; // Import toast for notifications
import {
  getOverlappingBreaksIntervalCheckByTime, // Import function to check overlapping breaks
  getOverlappingEventsIntervalCheckByTime, // Import function to check overlapping events
  isCurrentDateTimeGreater, // Import function to compare date/time
} from "../../../../constants/constants"; // Import utility functions
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Import booking status enum
import { useStore } from "../../../../contexts/store-provider"; // Import store context
import { formatMessage } from "../../../../translations/format-message"; // Import message formatter
import { FormattedMessage } from "react-intl";

const IntervalEvent: React.FC<{
  day: any; // Day object
  style: number; // Width of a single cell
  dataObject: any; // Data object containing event details
  allEvents: any[]; // List of all events
  allBreaks: any[]; // List of all breaks
  onResizingChange?: (isResizing: boolean) => void; // Callback for resizing state
  disabled: boolean; // Flag to disable
}> = ({
  style, // Width of a single cell
  onResizingChange, // Callback for resizing state
  dataObject, // Data object containing event details
  allEvents, // List of all events
  allBreaks, // List of all breaks
  day, // Day object
  disabled, // Flag to disable
}) => {
    const { reservationSchedulerStore, masterManagementStore } = useStore(); // Access store
    const { UpdateReservationPostBreakTimeService } = reservationSchedulerStore; // Access update service
    const { getAllTimeOptions, getReservationSettingDetail } = masterManagementStore; // Access time options and settings
    const divRef = useRef(null); // Reference to the div element
    const eventIntervalCellCountPostBreakTime = dataObject?.PostBreakTime / 5; // Calculate initial cell count
    const [cellCount, setCellCount] = useState(eventIntervalCellCountPostBreakTime); // State for cell count
    const [dragging, setDragging] = useState(false); // State for dragging
    const [resizing, setResizing] = useState(false); // State for resizing
    const [startX, setStartX] = useState(0); // Starting X position for resizing
    const [startCells, setStartCells] = useState(eventIntervalCellCountPostBreakTime); // Starting cell count for resizing
    const intervalTimeValue =
      Number(
        getAllTimeOptions?.find(
          (item: any) => item.id == getReservationSettingDetail?.IntervalTime
        )?.value
      ) || 0;

    const getClientX = (e: any) =>
      e?.touches?.[0]?.clientX ?? e?.changedTouches?.[0]?.clientX ?? e?.clientX;

    const getDayClosingTime = () => { // Function to get day's closing time
      const closingTime = getReservationSettingDetail?.BusinessSettings?.find(
        (item: any) => item?.WeekDays == new Date(dataObject?.Date)?.getDay() // Find closing time for the day
      );
      return closingTime?.ClosedTime; // Return closing time
    };


    const handleMouseDownResize = (e: any) => {
      e.stopPropagation();
      e.preventDefault();

      setResizing(true);
      setStartX(getClientX(e));
      setStartCells(cellCount);
      onResizingChange?.(true);
    };


    const handleMouseMove = (e: any) => {
      if (!resizing || disabled) return;

      e.preventDefault(); // prevents iPad scroll while dragging

      const dx = getClientX(e) - startX;
      const newCellCount = Math.max(startCells + Math.round(dx / style), 0);

      setCellCount(newCellCount);
    };

    const handleMouseUp = (e: any) => {
      if (!resizing || disabled) return;
      onResizingChange?.(false);
      setDragging(false);
      setResizing(false);

      const dx = getClientX(e) - startX;
      const newCellCount = Math.max(startCells + Math.round(dx / style), 0);

      const draggedTimeLabel = dataObject?.EndTime
        ? moment(dataObject.EndTime, "HH:mm")
          .add(newCellCount * 5, "minutes")
          .format("HH:mm")
        : "";

      const params = {
        date: localStorage.getItem("currentDate"),
        startTime: dataObject?.EndTime,
        endTime: draggedTimeLabel,
        staffId: Number(dataObject?.StaffId),
      };

      const matchedEvents = getOverlappingEventsIntervalCheckByTime(allEvents, params);
      let matchedBreaks = [];

      if (day.props["data-role"] == "daily") {
        matchedBreaks = getOverlappingBreaksIntervalCheckByTime(allBreaks, params);
      }

      const closingTime = moment(
        `${moment(dataObject?.Date).format("YYYY-MM-DD")} ${getDayClosingTime()}`,
        "YYYY-MM-DD HH:mm"
      );

      const appointmentEndTime = moment(
        `${moment(dataObject?.Date).format("YYYY-MM-DD")} ${dataObject?.EndTime
        }`,
        "YYYY-MM-DD HH:mm"
      );

      if (
        isCurrentDateTimeGreater(
          moment(day?.props["data-index"]).format("YYYY-MM-DD"),
          dataObject?.StartTime
        )
      ) {
        toast.error(formatMessage("cannot_update_past_events"));
        setCellCount(eventIntervalCellCountPostBreakTime);
      } else if (
        appointmentEndTime.add(newCellCount * 5, "minutes").isAfter(closingTime)
      ) {
        setCellCount(eventIntervalCellCountPostBreakTime);
        toast.error(formatMessage("cannot_drag_interval_outside_business_hours"));
      } else if (
        dataObject?.AppointmentStatus == bookingStatusEnum.Cancelled ||
        dataObject?.AppointmentStatus == bookingStatusEnum.Complete ||
        dataObject?.AppointmentStatus == bookingStatusEnum.InProcess
      ) {
        setCellCount(eventIntervalCellCountPostBreakTime);
        toast.error(formatMessage("interval_reserved_only"));
      } else if (matchedEvents?.length > 0 || matchedBreaks?.length > 0) {
        setCellCount(eventIntervalCellCountPostBreakTime);
        toast.error(formatMessage("interval_overlap_error"));
      } else {
        UpdateReservationPostBreakTimeService({
          Id: dataObject?.Id,
          PostBreakTime: newCellCount * 5,
        });
      }
    };

    const handleTouchMove = (e: TouchEvent) => handleMouseMove(e);
    const handleTouchEnd = (e: TouchEvent) => handleMouseUp(e);

    useEffect(() => {
      if (resizing) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        document.addEventListener("touchmove", handleTouchMove, { passive: false });
        document.addEventListener("touchend", handleTouchEnd);
      }

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);

        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }, [resizing]);

    return (
      <div
        ref={divRef} // Reference to the div element
        className="interval-box" // CSS class for styling
        style={{
          width: cellCount * style, // Dynamic width based on cell count
          cursor: dragging ? "grabbing" : "grab", // Cursor style based on dragging state
          userSelect: "none", // Disable text selection
        }}
      >
        {/* Resize handle */}
        <div
          onMouseDown={handleMouseDownResize} // Mouse down event for resizing
          style={{
            position: "absolute", // Positioning for resize handle
            top: 0, // Align to top
            right: 0, // Align to right
            width: 10, // Width of resize handle
            height: "100%", // Full height
            cursor: "ew-resize", // Resize cursor
            backgroundColor: "rgba(255,255,255,0.3)", // Semi-transparent background
          }}
        >
          <span className="interval_time">{cellCount * 5} <FormattedMessage id="min" /></span> {/* Show interval time */}
        </div>
      </div>
    );
  };

export default IntervalEvent; // Export the component
